import {
  Alert,
  Button,
  Code,
  Group,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconCircleCheck } from "@tabler/icons-react";
import { InlineError, SectionCard } from "../components/ui";
import { api } from "../lib/api";
import { Page } from "../shell/Page";
import { useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { PasswordChangeForm } from "../components/PasswordChangeForm";
import { roleLabel } from "../lib/roleLabels";


/**
 * Your own account: who you are, how you sign in, and the two things you may
 * change about yourself - your name, and your password.
 *
 * WHAT ANYBODY MAY CHANGE, AND WHAT ONLY AN ADMIN MAY
 *
 * Your NAME is yours. It was an administrator's job, which meant a misspelt
 * name needed somebody else's afternoon; the business moved it here on the
 * condition that every change is recorded, because a name is stamped into the
 * audit trail of everything its owner does.
 *
 * Username, mobile number and role are NOT. They are an administrator's, and
 * the reason is not that this screen declines to draw the field: `PATCH /auth/me`
 * accepts a name and nothing else.
 *
 * Which sign-in method you have is a property of your role, not a choice:
 * Admin and Store Manager use a username and password; every other role uses
 * mobile + OTP. So the password section appears only for the accounts that
 * actually have a password - offering it to a technician would be a form that
 * can only fail.
 */
export function Profile() {
  const { user, refresh } = useAuth();
  const [changing, setChanging] = useState(false);
  const [done, setDone] = useState(false);

  /**
   * Editing your own name.
   *
   * WHAT IS EDITABLE HERE AND WHAT IS NOT
   *
   * The name, and only the name. Username, mobile number and role stay
   * read-only and stay an administrator's to change - which is not merely a
   * matter of this screen not offering a field: `PATCH /auth/me` accepts a name
   * and nothing else, so there is no request this page could be made to send
   * that would reach them.
   */
  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState<string>();
  const [nameSaved, setNameSaved] = useState(false);

  if (!user) return null;

  const hasPassword = user.username !== null;
  const trimmed = draftName.trim();
  // The server applies the same rule. Checked here so the button is honest
  // about what will happen rather than offering a click that 400s.
  const nameValid = trimmed.length >= 2 && trimmed.length <= 200;

  async function saveName() {
    setSavingName(true);
    setNameError(undefined);
    try {
      await api.renameSelf(trimmed);
      // Re-reads /auth/me, which is what the greeting on Home and the app
      // shell are drawn from. Without it the old name stays on screen
      // everywhere except the field that was just edited.
      await refresh();
      setEditingName(false);
      setNameSaved(true);
    } catch (caught) {
      setNameError(
        caught instanceof Error ? caught.message : "Could not save your name.",
      );
    } finally {
      setSavingName(false);
    }
  }

  return (
    <Page title="Your profile" subtitle={roleLabel(user.role)}>
      <SectionCard title="Account">
        <Stack gap="sm">
          {nameSaved && !editingName && (
            <Alert
              variant="light"
              color="teal"
              radius="md"
              p="sm"
              icon={<IconCircleCheck size={18} stroke={1.8} />}
            >
              Your name has been saved. It is what other people in the app
              will see against your work from now on.
            </Alert>
          )}

          {editingName ? (
            <Stack gap="sm">
              <TextInput
                id="own-name"
                label="Your name"
                value={draftName}
                onChange={(event) => setDraftName(event.currentTarget.value)}
                maxLength={200}
                autoFocus
                description="How you appear to everybody else in the app."
                error={
                  draftName !== "" && !nameValid
                    ? "Enter at least two characters."
                    : undefined
                }
              />
              {nameError && <InlineError>{nameError}</InlineError>}
              <Group gap="sm" wrap="wrap">
                <Button
                  size="md"
                  disabled={!nameValid || savingName}
                  loading={savingName}
                  onClick={() => void saveName()}
                >
                  Save name
                </Button>
                <Button
                  variant="subtle"
                  color="gray"
                  size="md"
                  onClick={() => {
                    setEditingName(false);
                    setNameError(undefined);
                  }}
                >
                  Cancel
                </Button>
              </Group>
            </Stack>
          ) : null}

          <Stack gap={8} component="dl" m={0}>
            {!editingName && (
              <Row label="Name">
                <Group gap="xs" justify="flex-end" wrap="nowrap">
                  <span>{user.name}</span>
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    onClick={() => {
                      setDraftName(user.name);
                      setNameSaved(false);
                      setNameError(undefined);
                      setEditingName(true);
                    }}
                  >
                    Edit
                  </Button>
                </Group>
              </Row>
            )}
            <Row label="Role">{roleLabel(user.role)}</Row>
            {hasPassword ? (
              <>
                <Row label="Username">
                  <Code>{user.username}</Code>
                </Row>
                <Row label="Sign-in">Username and password</Row>
              </>
            ) : (
              <>
                <Row label="Mobile">
                  <span className="num">{user.mobileNumber ?? "—"}</span>
                </Row>
                <Row label="Sign-in">Mobile number and a one-time code by SMS</Row>
              </>
            )}
          </Stack>
          <Text fz="sm" c="gray.6">
            You can change your own name. Your role and your sign-in details —
            your {hasPassword ? "username" : "mobile number"} — are maintained
            by an administrator; ask them to change either of those.
          </Text>
        </Stack>
      </SectionCard>

      {hasPassword && (
        <SectionCard title="Password">
          <Stack gap="sm">
            {done && !changing && (
              <Alert
                variant="light"
                color="teal"
                radius="md"
                p="sm"
                icon={<IconCircleCheck size={18} stroke={1.8} />}
              >
                Your password has been changed. Use the new one next time you
                sign in.
              </Alert>
            )}

            {changing ? (
              <>
                <PasswordChangeForm
                  submitLabel="Change password"
                  onDone={async () => {
                    setChanging(false);
                    setDone(true);
                    // Re-reads /auth/me, which is what clears a must-change
                    // flag if this change satisfied one.
                    await refresh();
                  }}
                />
                <Button variant="subtle" color="gray" onClick={() => setChanging(false)}>
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Text fz="md" c="gray.7">
                  Change it whenever you like. You will need your current
                  password to confirm it is you.
                </Text>
                <Button
                  variant="default"
                  size="md"
                  fullWidth
                  onClick={() => {
                    setDone(false);
                    setChanging(true);
                  }}
                >
                  Change password
                </Button>
              </>
            )}
          </Stack>
        </SectionCard>
      )}
    </Page>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Group justify="space-between" align="flex-start" gap="md" wrap="nowrap">
      <Text component="dt" fz="sm" c="gray.6" style={{ flexShrink: 0 }}>
        {label}
      </Text>
      <Text component="dd" fz="sm" fw={500} c="gray.9" m={0} ta="right">
        {children}
      </Text>
    </Group>
  );
}
